import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiActivity,
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowRight,
  FiBox,
  FiCheckCircle,
  FiDollarSign,
  FiPackage,
  FiPlus,
  FiRefreshCcw,
  FiRotateCcw,
  FiShoppingBag,
  FiTrendingDown,
  FiTrendingUp,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";

import "./InventoryDashboard.css";


/* =========================================================
   EMPTY SUMMARY
========================================================= */

const EMPTY_SUMMARY = {
  products: {
    totalProducts: 0,

    activeProducts: 0,

    inactiveProducts: 0,

    draftProducts: 0,

    totalStockQty: 0,

    totalReservedQty: 0,

    totalAvailableQty: 0,

    outOfStockProducts: 0,

    lowStockProducts: 0,

    inStockProducts: 0,

    totalPurchaseValue: 0,

    totalSellingValue: 0,

    availableSellingValue: 0,

    reservedSellingValue: 0,
  },

  movements: {
    totalStockIn: 0,

    totalStockOut: 0,

    totalMovements: 0,

    netMovement: 0,

    movementTypes: {},
  },

  estimatedProfit: 0,

  availableEstimatedProfit: 0,

  recentMovements: [],
};


/* =========================================================
   CURRENCY
========================================================= */

const formatCurrency = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN",
    {
      style: "currency",

      currency: "INR",

      maximumFractionDigits: 0,
    }
  ).format(
    Number(
      value || 0
    )
  );
};


/* =========================================================
   NUMBER
========================================================= */

const formatNumber = (
  value
) => {
  return new Intl.NumberFormat(
    "en-IN"
  ).format(
    Number(
      value || 0
    )
  );
};


/* =========================================================
   DATE
========================================================= */

const formatDate = (
  value
) => {
  if (!value) {
    return "-";
  }


  const date =
    new Date(
      value
    );


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-";
  }


  return new Intl.DateTimeFormat(
    "en-IN",
    {
      day: "2-digit",

      month: "short",

      year: "numeric",

      hour: "2-digit",

      minute: "2-digit",
    }
  ).format(
    date
  );
};


/* =========================================================
   MOVEMENT LABEL
========================================================= */

const getMovementLabel = (
  movementType
) => {
  const labels = {
    opening_stock:
      "Opening Stock",

    stock_in:
      "Stock In",

    stock_out:
      "Stock Out",

    sale:
      "Sale",

    sale_cancelled:
      "Sale Cancelled",

    customer_return:
      "Customer Return",

    supplier_return:
      "Supplier Return",

    damaged:
      "Damaged",

    lost:
      "Lost",

    adjustment_in:
      "Adjustment In",

    adjustment_out:
      "Adjustment Out",
  };


  return (
    labels[
      movementType
    ] ||
    String(
      movementType ||
      "-"
    )
      .replace(
        /_/g,
        " "
      )
      .replace(
        /\b\w/g,
        (
          character
        ) =>
          character.toUpperCase()
      )
  );
};


/* =========================================================
   MOVEMENT DIRECTION
========================================================= */

const isStockInMovement = (
  movementType
) => {
  return [
    "opening_stock",

    "stock_in",

    "sale_cancelled",

    "customer_return",

    "adjustment_in",
  ].includes(
    movementType
  );
};


/* =========================================================
   MOVEMENT STYLE
========================================================= */

const getMovementTone = (
  movementType
) => {
  if (
    movementType ===
      "damaged" ||
    movementType ===
      "lost"
  ) {
    return "danger";
  }


  if (
    movementType ===
      "supplier_return"
  ) {
    return "warning";
  }


  if (
    movementType ===
      "adjustment_in" ||
    movementType ===
      "adjustment_out"
  ) {
    return "adjustment";
  }


  return isStockInMovement(
    movementType
  )
    ? "stock-in"
    : "stock-out";
};


/* =========================================================
   MOVEMENT QUANTITY
========================================================= */

const getMovementQuantity = (
  movementTypes,
  type
) => {
  return Number(
    movementTypes?.[
      type
    ]?.quantity ||
    0
  );
};


/* =========================================================
   COMPONENT

   All callbacks are optional.

   Later InventoryManagement can pass:

   onAddProduct
   onAdjustStock
   onAddCategory
   onViewLowStock
   onViewProducts
   onViewMovements
========================================================= */

const InventoryDashboard = ({
  onAddProduct,

  onAdjustStock,

  onAddCategory,

  onViewLowStock,

  onViewProducts,

  onViewMovements,
}) => {
  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  /* =======================================================
     SUMMARY
  ======================================================= */

  const [
    summary,
    setSummary,
  ] =
    useState({
      ...EMPTY_SUMMARY,

      products: {
        ...EMPTY_SUMMARY.products,
      },

      movements: {
        ...EMPTY_SUMMARY.movements,
      },

      recentMovements: [],
    });


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] =
    useState(false);


  /* =======================================================
     FIRST LOAD
  ======================================================= */

  const [
    initialized,
    setInitialized,
  ] =
    useState(false);


  /* =======================================================
     ERROR
  ======================================================= */

  const [
    error,
    setError,
  ] =
    useState("");


  /* =======================================================
     LAST UPDATED
  ======================================================= */

  const [
    lastUpdated,
    setLastUpdated,
  ] =
    useState(null);


  /* =======================================================
     FETCH SUMMARY
  ======================================================= */

  const fetchSummary =
    useCallback(
      async () => {
        try {
          setLoading(
            true
          );

          setError(
            ""
          );


          const response =
            await api.get(
              config
                .getInventorySummary
            );


          if (
            response?.status !==
            "success"
          ) {
            throw new Error(
              response?.message ||
              "Unable to fetch inventory summary."
            );
          }


          const responseData =
            response?.data ||
            {};


          setSummary({
            ...EMPTY_SUMMARY,

            ...responseData,

            products: {
              ...EMPTY_SUMMARY.products,

              ...(responseData
                ?.products ||
                {}),
            },

            movements: {
              ...EMPTY_SUMMARY.movements,

              ...(responseData
                ?.movements ||
                {}),
            },

            recentMovements:
              Array.isArray(
                responseData
                  ?.recentMovements
              )
                ? responseData
                    .recentMovements
                : [],
          });


          setLastUpdated(
            new Date()
          );


          setInitialized(
            true
          );

        } catch (
          requestError
        ) {
          console.error(
            "Inventory dashboard error:",
            requestError
          );


          /*
           * IMPORTANT:
           *
           * Do not erase previously loaded
           * dashboard values if refresh fails.
           */

          setError(
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to refresh inventory dashboard."
          );


          setInitialized(
            true
          );

        } finally {
          setLoading(
            false
          );
        }
      },
      [
        api,
      ]
    );


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(
    () => {
      fetchSummary();
    },
    [
      fetchSummary,
    ]
  );


  /* =======================================================
     STOCK VALUES
  ======================================================= */

  const physicalStock =
    Number(
      summary
        .products
        .totalStockQty ||
      0
    );


  const reservedStock =
    Number(
      summary
        .products
        .totalReservedQty ||
      0
    );


  /*
   * Prefer backend available quantity.
   *
   * Fallback for older API response.
   */

  const availableStock =
    summary
      .products
      .totalAvailableQty !==
      undefined
      ? Number(
          summary
            .products
            .totalAvailableQty ||
          0
        )
      : Math.max(
          physicalStock -
            reservedStock,

          0
        );


  /* =======================================================
     STOCK ATTENTION
  ======================================================= */

  const lowStockCount =
    Number(
      summary
        .products
        .lowStockProducts ||
      0
    );


  const outOfStockCount =
    Number(
      summary
        .products
        .outOfStockProducts ||
      0
    );


  const requiresAttention =
    lowStockCount >
      0 ||
    outOfStockCount >
      0;


  /* =======================================================
     CARDS
  ======================================================= */

  const cards = [
    {
      key:
        "products",

      title:
        "Total Products",

      value:
        formatNumber(
          summary
            .products
            .totalProducts
        ),

      subtitle:
        `${formatNumber(
          summary
            .products
            .activeProducts
        )} active products`,

      icon:
        FiPackage,

      className:
        "blue",

      onClick:
        onViewProducts,
    },


    {
      key:
        "physical-stock",

      title:
        "Physical Stock",

      value:
        formatNumber(
          physicalStock
        ),

      subtitle:
        `${formatNumber(
          reservedStock
        )} units reserved`,

      icon:
        FiBox,

      className:
        "purple",
    },


    {
      key:
        "available-stock",

      title:
        "Available Stock",

      value:
        formatNumber(
          availableStock
        ),

      subtitle:
        "Available for sale",

      icon:
        FiCheckCircle,

      className:
        "cyan",
    },


    {
      key:
        "purchase-value",

      title:
        "Purchase Value",

      value:
        formatCurrency(
          summary
            .products
            .totalPurchaseValue
        ),

      subtitle:
        "Current inventory cost",

      icon:
        FiShoppingBag,

      className:
        "orange",
    },


    {
      key:
        "selling-value",

      title:
        "Selling Value",

      value:
        formatCurrency(
          summary
            .products
            .totalSellingValue
        ),

      subtitle:
        "Physical stock value",

      icon:
        FiDollarSign,

      className:
        "green",
    },


    {
      key:
        "estimated-profit",

      title:
        "Estimated Profit",

      value:
        formatCurrency(
          summary
            .estimatedProfit
        ),

      subtitle:
        "Selling value minus cost",

      icon:
        FiActivity,

      className:
        "profit",
    },
  ];


  /* =======================================================
     MOVEMENT TYPES
  ======================================================= */

  const movementTypes =
    summary
      ?.movements
      ?.movementTypes ||
    {};


  const saleQty =
    getMovementQuantity(
      movementTypes,
      "sale"
    );


  const customerReturnQty =
    getMovementQuantity(
      movementTypes,
      "customer_return"
    );


  const supplierReturnQty =
    getMovementQuantity(
      movementTypes,
      "supplier_return"
    );


  const damagedQty =
    getMovementQuantity(
      movementTypes,
      "damaged"
    );


  const lostQty =
    getMovementQuantity(
      movementTypes,
      "lost"
    );


  const adjustmentQty =
    getMovementQuantity(
      movementTypes,
      "adjustment_in"
    ) +
    getMovementQuantity(
      movementTypes,
      "adjustment_out"
    );


  /* =======================================================
     INITIAL LOADING
  ======================================================= */

  if (
    !initialized &&
    loading
  ) {
    return (
      <div
        className="inventory-dashboard-page"
      >
        <div
          className="inventory-dashboard-initial-loading"
        >
          <span
            className="inventory-dashboard-spinner"
          />

          <strong>
            Loading inventory dashboard
          </strong>

          <span>
            Preparing your inventory overview...
          </span>
        </div>
      </div>
    );
  }


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="inventory-dashboard-page"
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <div
        className="inventory-dashboard-header"
      >
        <div
          className="inventory-dashboard-heading"
        >
          <span>
            INVENTORY OVERVIEW
          </span>


          <h3>
            Dashboard
          </h3>


          <p>
            Monitor products, available stock,
            inventory health, value and recent
            stock activity.
          </p>
        </div>


        <div
          className="inventory-dashboard-header-right"
        >
          {lastUpdated ? (
            <div
              className="inventory-dashboard-last-updated"
            >
              <span>
                Last updated
              </span>

              <strong>
                {formatDate(
                  lastUpdated
                )}
              </strong>
            </div>
          ) : null}


          <button
            type="button"
            className="inventory-dashboard-refresh"
            onClick={
              fetchSummary
            }
            disabled={
              loading
            }
          >
            <FiRefreshCcw
              className={
                loading
                  ? "spinning"
                  : ""
              }
            />

            {loading
              ? "Refreshing..."
              : "Refresh"}
          </button>
        </div>
      </div>


      {/* ===================================================
          ERROR
      =================================================== */}

      {error ? (
        <div
          className="inventory-dashboard-error"
        >
          <FiAlertCircle />

          <div>
            <strong>
              Dashboard refresh failed
            </strong>

            <span>
              {error}
            </span>
          </div>


          <button
            type="button"
            onClick={
              fetchSummary
            }
          >
            Try Again
          </button>
        </div>
      ) : null}



      {/* ===================================================
          SUMMARY CARDS
      =================================================== */}

      <div
        className="inventory-dashboard-card-grid"
      >
        {cards.map(
          (
            card
          ) => {
            const Icon =
              card.icon;


            const clickable =
              typeof card.onClick ===
              "function";


            return (
              <button
                key={
                  card.key
                }
                type="button"
                className={`inventory-dashboard-card inventory-dashboard-card-${card.className} ${
                  clickable
                    ? "clickable"
                    : ""
                }`}
                onClick={
                  card.onClick
                }
                disabled={
                  !clickable
                }
              >
                <div
                  className="inventory-dashboard-card-icon"
                >
                  <Icon />
                </div>


                <div
                  className="inventory-dashboard-card-content"
                >
                  <span>
                    {
                      card.title
                    }
                  </span>


                  <strong>
                    {card.value}
                  </strong>


                  <small>
                    {
                      card.subtitle
                    }
                  </small>
                </div>


                {clickable ? (
                  <FiArrowRight
                    className="inventory-dashboard-card-arrow"
                  />
                ) : null}
              </button>
            );
          }
        )}
      </div>


      {/* ===================================================
          INVENTORY ATTENTION
      =================================================== */}

      <section
        className={`inventory-dashboard-attention ${
          requiresAttention
            ? "has-warning"
            : "healthy"
        }`}
      >
        <div
          className="inventory-dashboard-attention-icon"
        >
          {requiresAttention ? (
            <FiAlertTriangle />
          ) : (
            <FiCheckCircle />
          )}
        </div>


        <div
          className="inventory-dashboard-attention-content"
        >
          <span>
            INVENTORY HEALTH
          </span>


          <strong>
            {requiresAttention
              ? "Stock attention required"
              : "Inventory levels look healthy"}
          </strong>


          <p>
            {requiresAttention
              ? "Some products require inventory attention."
              : "There are currently no low-stock or out-of-stock alerts."}
          </p>
        </div>


        <div
          className="inventory-dashboard-attention-stats"
        >
          <div>
            <span>
              Low Stock
            </span>

            <strong>
              {formatNumber(
                lowStockCount
              )}
            </strong>
          </div>


          <div>
            <span>
              Out of Stock
            </span>

            <strong>
              {formatNumber(
                outOfStockCount
              )}
            </strong>
          </div>
        </div>


        {typeof onViewLowStock ===
        "function" ? (
          <button
            type="button"
            onClick={
              onViewLowStock
            }
          >
            View Products

            <FiArrowRight />
          </button>
        ) : null}
      </section>


      {/* ===================================================
          MAIN GRID
      =================================================== */}

      <div
        className="inventory-dashboard-content-grid"
      >
        {/* =================================================
            INVENTORY FLOW
        ================================================= */}

        <section
          className="inventory-dashboard-panel"
        >
          <div
            className="inventory-dashboard-panel-header"
          >
            <div>
              <span>
                STOCK MOVEMENT
              </span>

              <h4>
                Inventory Flow
              </h4>

              <p>
                Overall stock movement breakdown
              </p>
            </div>


            {typeof onViewMovements ===
            "function" ? (
              <button
                type="button"
                onClick={
                  onViewMovements
                }
              >
                View History

                <FiArrowRight />
              </button>
            ) : null}
          </div>


          {/* ===============================================
              PRIMARY FLOW
          =============================================== */}

          <div
            className="inventory-dashboard-flow-grid"
          >
            <div
              className="inventory-dashboard-flow-card stock-in"
            >
              <div>
                <FiTrendingUp />
              </div>

              <span>
                Stock In
              </span>

              <strong>
                {formatNumber(
                  summary
                    .movements
                    .totalStockIn
                )}
              </strong>
            </div>


            <div
              className="inventory-dashboard-flow-card stock-out"
            >
              <div>
                <FiTrendingDown />
              </div>

              <span>
                Stock Out
              </span>

              <strong>
                {formatNumber(
                  summary
                    .movements
                    .totalStockOut
                )}
              </strong>
            </div>


            <div
              className="inventory-dashboard-flow-card movement-count"
            >
              <div>
                <FiActivity />
              </div>

              <span>
                Movements
              </span>

              <strong>
                {formatNumber(
                  summary
                    .movements
                    .totalMovements
                )}
              </strong>
            </div>
          </div>


          {/* ===============================================
              MOVEMENT BREAKDOWN
          =============================================== */}

          <div
            className="inventory-dashboard-breakdown"
          >
            <div>
              <span
                className="sale"
              >
                <FiShoppingBag />
              </span>

              <div>
                <small>
                  Sales
                </small>

                <strong>
                  {formatNumber(
                    saleQty
                  )}
                </strong>
              </div>
            </div>


            <div>
              <span
                className="return"
              >
                <FiRotateCcw />
              </span>

              <div>
                <small>
                  Customer Returns
                </small>

                <strong>
                  {formatNumber(
                    customerReturnQty
                  )}
                </strong>
              </div>
            </div>


            <div>
              <span
                className="supplier"
              >
                <FiTrendingDown />
              </span>

              <div>
                <small>
                  Supplier Returns
                </small>

                <strong>
                  {formatNumber(
                    supplierReturnQty
                  )}
                </strong>
              </div>
            </div>


            <div>
              <span
                className="danger"
              >
                <FiAlertTriangle />
              </span>

              <div>
                <small>
                  Damaged / Lost
                </small>

                <strong>
                  {formatNumber(
                    damagedQty +
                    lostQty
                  )}
                </strong>
              </div>
            </div>


            <div>
              <span
                className="adjustment"
              >
                <FiActivity />
              </span>

              <div>
                <small>
                  Adjustments
                </small>

                <strong>
                  {formatNumber(
                    adjustmentQty
                  )}
                </strong>
              </div>
            </div>
          </div>


          {/* ===============================================
              PRODUCT STATUS
          =============================================== */}

          <div
            className="inventory-dashboard-status-heading"
          >
            Product Status
          </div>


          <div
            className="inventory-dashboard-product-status"
          >
            <div
              className="active"
            >
              <span>
                Active
              </span>

              <strong>
                {formatNumber(
                  summary
                    .products
                    .activeProducts
                )}
              </strong>
            </div>


            <div
              className="inactive"
            >
              <span>
                Inactive
              </span>

              <strong>
                {formatNumber(
                  summary
                    .products
                    .inactiveProducts
                )}
              </strong>
            </div>


            <div
              className="draft"
            >
              <span>
                Draft
              </span>

              <strong>
                {formatNumber(
                  summary
                    .products
                    .draftProducts
                )}
              </strong>
            </div>


            <div
              className="out"
            >
              <span>
                Out of Stock
              </span>

              <strong>
                {formatNumber(
                  outOfStockCount
                )}
              </strong>
            </div>
          </div>
        </section>


        {/* =================================================
            RECENT MOVEMENTS
        ================================================= */}

        <section
          className="inventory-dashboard-panel"
        >
          <div
            className="inventory-dashboard-panel-header"
          >
            <div>
              <span>
                RECENT ACTIVITY
              </span>

              <h4>
                Latest Stock Movements
              </h4>

              <p>
                Most recent inventory transactions
              </p>
            </div>


            {typeof onViewMovements ===
            "function" ? (
              <button
                type="button"
                onClick={
                  onViewMovements
                }
              >
                View All

                <FiArrowRight />
              </button>
            ) : null}
          </div>


          <div
            className="inventory-dashboard-movement-list"
          >
            {summary
              .recentMovements
              .length ===
            0 ? (
              <div
                className="inventory-dashboard-empty"
              >
                <FiActivity />

                <strong>
                  No stock activity
                </strong>

                <span>
                  Stock movements will appear
                  here after inventory changes.
                </span>
              </div>
            ) : (
              summary
                .recentMovements
                .map(
                  (
                    movement
                  ) => {
                    const stockIn =
                      isStockInMovement(
                        movement
                          .movementType
                      );


                    const tone =
                      getMovementTone(
                        movement
                          .movementType
                      );


                    return (
                      <div
                        key={
                          movement
                            ._id
                        }
                        className="inventory-dashboard-movement-item"
                      >
                        <div
                          className={`inventory-dashboard-movement-icon ${tone}`}
                        >
                          {stockIn ? (
                            <FiTrendingUp />
                          ) : (
                            <FiTrendingDown />
                          )}
                        </div>


                        <div
                          className="inventory-dashboard-movement-info"
                        >
                          <strong>
                            {movement
                              ?.product
                              ?.name ||
                              "Unknown Product"}
                          </strong>


                          <span>
                            {getMovementLabel(
                              movement
                                .movementType
                            )}

                            {" • "}

                            Qty:{" "}

                            {
                              movement
                                .quantity
                            }
                          </span>


                          <small>
                            SKU:{" "}

                            {movement
                              ?.product
                              ?.sku ||
                              "-"}

                            {movement
                              ?.referenceNumber
                              ? ` • Ref: ${movement.referenceNumber}`
                              : ""}
                          </small>
                        </div>


                        <div
                          className="inventory-dashboard-movement-date"
                        >
                          <strong
                            className={
                              stockIn
                                ? "positive"
                                : "negative"
                            }
                          >
                            {stockIn
                              ? "+"
                              : "-"}

                            {
                              movement
                                .quantity
                            }
                          </strong>


                          <span>
                            {formatDate(
                              movement
                                .createdAt
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  }
                )
            )}
          </div>
        </section>
      </div>
    </div>
  );
};


export default InventoryDashboard;